import "./dom-env";

import { cssColorToHex } from "./css-color";
import type { Layer } from "./model";
import { readStyle } from "./parse";

export interface TextSegment {
  text: string;
  color: string;
  fontWeight: string;
  italic: boolean;
  underline: boolean;
  /**
   * PSD export only: absent unless the caller's base carries the layer's size, which is what says
   * run sizes are wanted. The Figma round-trip omits it, so its segments never split on size and a
   * plugin write-back can't drop a `font-size` span it doesn't know about. Only `px` parses; see
   * {@link readPxStyle}.
   */
  fontSize?: number;
}

export type SegmentStyle = Pick<
  TextSegment,
  "color" | "fontWeight" | "italic" | "underline" | "fontSize"
>;

const parser = new DOMParser();

type Break = { kind: "br" } | { kind: "block" };
type Token = { kind: "text"; text: string; style: SegmentStyle } | Break;

export function segmentsFromContent(args: {
  content: string;
  base: SegmentStyle;
}): TextSegment[] {
  const dom = parser.parseFromString(
    `<!DOCTYPE html><body>${args.content}</body>`,
    "text/html",
  );
  const tokens: Token[] = [];
  collectTokens(dom.body, args.base, tokens);

  const lines: { text: string; style: SegmentStyle }[][] = [[]];
  const boundaries: Break[] = [];
  for (const token of tokens) {
    if (token.kind === "text") lines[lines.length - 1]!.push(token);
    else {
      boundaries.push(token);
      lines.push([]);
    }
  }

  const segments: TextSegment[] = [];
  let pendingBrs = 0;
  let pendingBlocks = 0;
  let leadingBreaks = "";
  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      if (boundaries[lineIndex - 1]!.kind === "br") pendingBrs += 1;
      else pendingBlocks += 1;
    }
    const runs = normalizeLine(line);
    if (runs.length === 0) return;
    const count =
      segments.length === 0
        ? pendingBrs
        : Math.max(pendingBrs, pendingBlocks > 0 ? 1 : 0);
    const breaks = "\n".repeat(count);
    const last = segments[segments.length - 1];
    if (last) last.text += breaks;
    else leadingBreaks = breaks;
    pendingBrs = 0;
    pendingBlocks = 0;
    for (const run of runs) {
      const tail = segments[segments.length - 1];
      if (tail && sameStyle(tail, run.style)) {
        tail.text += run.text;
      } else {
        segments.push({ text: leadingBreaks + run.text, ...run.style });
        leadingBreaks = "";
      }
    }
  });
  const trailing = "\n".repeat(pendingBrs);
  if (trailing) {
    const last = segments[segments.length - 1];
    if (last) last.text += trailing;
    else segments.push({ text: trailing, ...args.base });
  }
  return segments;
}

function collectTokens(
  parent: Element,
  style: SegmentStyle,
  out: Token[],
): void {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      if (text) out.push({ kind: "text", text, style });
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const el = node as Element;
    if (el.tagName.toLowerCase() === "br") {
      out.push({ kind: "br" });
      continue;
    }
    const isBlock = readStyle(el, "display") === "block";
    if (isBlock) out.push({ kind: "block" });
    collectTokens(el, cascadeStyle(style, el), out);
    if (isBlock) out.push({ kind: "block" });
  }
}

function cascadeStyle(inherited: SegmentStyle, el: Element): SegmentStyle {
  const tag = el.tagName.toLowerCase();
  const style = { ...inherited };
  if (tag === "b" || tag === "strong") style.fontWeight = "700";
  if (tag === "i" || tag === "em") style.italic = true;
  if (tag === "u") style.underline = true;

  const color = readStyle(el, "color");
  if (color) {
    style.color = (cssColorToHex(color) ?? color).toLowerCase();
  }
  const weight = readStyle(el, "font-weight");
  if (weight) style.fontWeight = resolveFontWeight(weight);
  if (inherited.fontSize !== undefined) {
    const fontSize = readPxStyle(el, "font-size");
    if (fontSize !== null) style.fontSize = fontSize;
  }
  const fontStyle = readStyle(el, "font-style");
  if (fontStyle === "italic" || fontStyle === "oblique") style.italic = true;
  else if (fontStyle === "normal") style.italic = false;
  const decoration =
    readStyle(el, "text-decoration") || readStyle(el, "text-decoration-line");
  if (decoration) {
    const parts = decoration.split(/\s+/);
    if (parts.includes("underline")) style.underline = true;
    else if (parts.includes("none")) style.underline = false;
  }
  return style;
}

/**
 * A `px` font size. Other units are dropped rather than guessed at: resolving `em`/`%` needs the
 * inherited size in layout, and the simplifier emits computed CSS, which is always `px`.
 */
function readPxStyle(el: Element, prop: string): number | null {
  const raw = readStyle(el, prop)?.trim();
  if (!raw) return null;
  const match = /^(-?\d*\.?\d+)px$/.exec(raw);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

function resolveFontWeight(weight: string): string {
  if (weight === "bold") return "700";
  if (weight === "normal") return "400";
  return weight;
}

function normalizeLine(
  runs: { text: string; style: SegmentStyle }[],
): { text: string; style: SegmentStyle }[] {
  const out: { text: string; style: SegmentStyle }[] = [];
  for (const run of runs) {
    let text = run.text.replace(/[ \t\n\r\f]+/g, " ");
    const prev = out[out.length - 1];
    if (text.startsWith(" ") && (!prev || prev.text.endsWith(" "))) {
      text = text.slice(1);
    }
    if (!text) continue;
    if (prev && sameStyle(prev.style, run.style)) prev.text += text;
    else out.push({ text, style: run.style });
  }
  while (out.length > 0) {
    const last = out[out.length - 1]!;
    last.text = last.text.replace(/ +$/, "");
    if (last.text) break;
    out.pop();
  }
  return out;
}

function sameStyle(a: SegmentStyle, b: SegmentStyle): boolean {
  return (
    a.color === b.color &&
    a.fontWeight === b.fontWeight &&
    a.italic === b.italic &&
    a.underline === b.underline &&
    a.fontSize === b.fontSize
  );
}

export function contentFromSegments(args: {
  segments: TextSegment[];
  base: SegmentStyle;
}): string {
  return args.segments
    .map((segment, index) => {
      const declarations = overrideDeclarations(segment, args.base);
      const startsLine =
        index === 0 || Boolean(args.segments[index - 1]?.text.endsWith("\n"));
      const endsLine = index === args.segments.length - 1;
      const pieces = segment.text.split("\n");
      return pieces
        .map((piece, pieceIndex) => {
          if (!piece) return "";
          const hardened = hardenLineEdgeSpaces(piece, {
            atLineStart: pieceIndex > 0 || startsLine,
            atLineEnd: pieceIndex < pieces.length - 1 || endsLine,
          });
          const text = escapeTextContent(hardened);
          return declarations.length > 0
            ? `<span style="${escapeAttr(declarations.join("; "))}">${text}</span>`
            : text;
        })
        .join("<br>");
    })
    .join("");
}

/** A collapsible space at a line-box edge doesn't render; NBSP does, and lowers back as content. */
function hardenLineEdgeSpaces(
  piece: string,
  edges: { atLineStart: boolean; atLineEnd: boolean },
): string {
  let out = piece;
  if (edges.atLineStart) {
    out = out.replace(/^ +/, (run) => "\u00a0".repeat(run.length));
  }
  if (edges.atLineEnd) {
    out = out.replace(/ +$/, (run) => "\u00a0".repeat(run.length));
  }
  return out;
}

function overrideDeclarations(
  segment: TextSegment,
  base: SegmentStyle,
): string[] {
  const declarations: string[] = [];
  if (segment.color !== base.color) {
    declarations.push(`color: ${segment.color}`);
  }
  if (segment.fontWeight !== base.fontWeight) {
    declarations.push(`font-weight: ${segment.fontWeight}`);
  }
  if (segment.italic !== base.italic) {
    declarations.push(`font-style: ${segment.italic ? "italic" : "normal"}`);
  }
  if (segment.underline !== base.underline) {
    declarations.push(
      `text-decoration: ${segment.underline ? "underline" : "none"}`,
    );
  }
  // A size that parses in has to serialize back out, or the inverse law breaks. `undefined` means
  // "inherit the layer's size", which is what emitting nothing already says.
  if (segment.fontSize !== undefined && segment.fontSize !== base.fontSize) {
    declarations.push(`font-size: ${segment.fontSize}px`);
  }
  return declarations;
}

function escapeTextContent(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

// Raising re-emits a full <span style> wrapper per newline-split piece, so
// untrusted PUT bodies could amplify ~40x into serialized markup. These bounds
// reject anything beyond a real text layer before any string is built.
const MAX_SEGMENTS_PER_LAYER = 1000;
const MAX_SEGMENT_TEXT_CHARS = 10_000;
const MAX_LAYER_TEXT_CHARS = 50_000;

const validatedSegments = (
  segments: TextSegment[],
  layerId: string,
): TextSegment[] => {
  if (segments.length > MAX_SEGMENTS_PER_LAYER) {
    throw new Error(`Too many text segments (layer ${layerId})`);
  }
  let layerTextChars = 0;
  return segments.map((segment, index) => {
    const where = `layer ${layerId} segment ${index}`;
    if (
      typeof segment.text !== "string" ||
      typeof segment.italic !== "boolean" ||
      typeof segment.underline !== "boolean"
    ) {
      throw new Error(`Malformed text segment (${where})`);
    }
    if (segment.text.length > MAX_SEGMENT_TEXT_CHARS) {
      throw new Error(`Text segment too long (${where})`);
    }
    layerTextChars += segment.text.length;
    if (layerTextChars > MAX_LAYER_TEXT_CHARS) {
      throw new Error(`Too much text in layer ${layerId}`);
    }
    if (/\n{101}/.test(segment.text)) {
      throw new Error(`Too many consecutive line breaks (${where})`);
    }
    if (
      typeof segment.fontWeight !== "string" ||
      !/^\d{1,4}$/.test(segment.fontWeight) ||
      Number(segment.fontWeight) < 1 ||
      Number(segment.fontWeight) > 1000
    ) {
      throw new Error(`Invalid font weight in text segment (${where})`);
    }
    const color =
      typeof segment.color === "string"
        ? (cssColorToHex(segment.color) ?? "").toLowerCase()
        : "";
    if (!/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(color)) {
      throw new Error(`Invalid color in text segment (${where})`);
    }
    // Run size is a PSD-export concern; a caller's is dropped rather than validated and written
    // into the stored markup.
    const { fontSize: _runSize, ...rest } = segment;
    return { ...rest, color };
  });
};

export function applySegmentsToTextLayers(layers: Layer[]): void {
  for (const layer of layers) {
    if (layer.type === "group") {
      applySegmentsToTextLayers(layer.children);
      continue;
    }
    if (layer.type !== "text" || !layer.figmaTextSegments) continue;
    const segments = validatedSegments(layer.figmaTextSegments, layer.id);
    const base = {
      color: layer.color,
      fontWeight: layer.fontWeight,
      italic: layer.italic,
      underline: layer.underline,
    };
    const stored = segmentsFromContent({ content: layer.content, base });
    if (!segmentListsEqual(segments, stored)) {
      layer.content = contentFromSegments({ segments, base });
    }
  }
}

function segmentListsEqual(a: TextSegment[], b: TextSegment[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (segment, i) => segment.text === b[i]!.text && sameStyle(segment, b[i]!),
    )
  );
}
